import { MetaRating } from "@/api/privateRequests/squad-builder/get-meta-ratings";
import Button from "@/components/Button";
import { PlusPlus } from "@/components/molecules/PlusPlus/PlusPlus";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { star } from "@/figmaElements";
import { uniquePositionScores } from "@/functions/getFormation";
import { cn } from "@/lib/utils";
import {
  PlayerRole,
  getRoleFromPlusPlus,
  getRolesPlusOrPlusPlus,
  matchRoleIdFromPlusPlus,
  playerRoleLabelSelector,
  playersSelector,
  tierSelector,
} from "@/redux/players/playersSlice";
import { BREAKPOINTS } from "@/settings";
import { useSelector } from "react-redux";
import { useMediaQuery } from "usehooks-ts";

import { GridBox } from "../DetailedPlayer";
import { Chemistry, DetailedPlayerAction } from "../reducer";
import { getBestMetaRatingAndRole } from "./const AllRolesSection";

export type RoleWithBest = {
  playerRoleId: number;
  best: boolean;
  metaRating: string;
  metaRatingZeroChem: string;
  chemstyleId: number;
  plus?: boolean;
  plusPlus?: boolean;
  position?: string;
};

const groupAndSortRolesByPosition = (
  rolesPlus: number[],
  metaRatingsAllRoles: MetaRating[],
  chemistry: Chemistry,
  showAllRoles?: boolean,
  positions?: string[],
) => {
  const grouped: Record<string, RoleWithBest[]> = {};
  const { playerRoles } = useSelector(playersSelector);

  const bestRoleAndMetaRating = getBestMetaRatingAndRole({
    metaRatings: metaRatingsAllRoles,
    chemistry,
  });

  const rolesToProcess = showAllRoles
    ? playerRoles?.filter((role) => positions?.includes(role.position))
    : rolesPlus;

  rolesToProcess?.forEach((roleOrId) => {
    const role = showAllRoles
      ? (roleOrId as PlayerRole)
      : getRoleFromPlusPlus({
          playerRoleIdFromPlusPlus: roleOrId as number,
          playerRoles,
        });

    if (!role) {
      return;
    }

    const metaRating = metaRatingsAllRoles.find(
      (metaRole) =>
        matchRoleIdFromPlusPlus({
          playerRoleId: metaRole.playerRoleId,
          playerRoleIdFromPlusPlus: role.id,
        }) && metaRole.chemistry === chemistry,
    );

    const metaRatingZeroChem = metaRatingsAllRoles.find(
      (metaRole) =>
        matchRoleIdFromPlusPlus({
          playerRoleId: metaRole.playerRoleId,
          playerRoleIdFromPlusPlus: role.id,
        }) && metaRole.chemistry === 0,
    );

    if (!grouped[role.position]) {
      grouped[role.position] = [];
    }

    const rolesPlusOrPlusPlus = showAllRoles
      ? getRolesPlusOrPlusPlus({
          playerRoleId: role.id,
          rolesPlus,
        })
      : getRolesPlusOrPlusPlus({
          playerRoleId: role.id,
          rolesPlus,
        });

    grouped[role.position].push({
      playerRoleId: role.id,
      best: role.id === bestRoleAndMetaRating?.playerRoleId,
      metaRating: metaRating?.metaRating?.toFixed(1),
      metaRatingZeroChem: metaRatingZeroChem?.metaRating?.toFixed(1),
      chemstyleId: metaRating?.chemstyleId,
      plus: rolesPlusOrPlusPlus === "plus",
      plusPlus: rolesPlusOrPlusPlus === "plusPlus",
    });
  });

  const sortedEntries = Object.entries(grouped).sort(
    ([posA], [posB]) =>
      (uniquePositionScores[posA] ?? 999) - (uniquePositionScores[posB] ?? 999),
  );

  const sortedGrouped: Record<string, RoleWithBest[]> = {};
  for (const [pos, roles] of sortedEntries) {
    sortedGrouped[pos] = roles;
  }
  return sortedGrouped;
};

const Role = ({
  role,
  handleAction,
  isActive,
  isDisabled,
}: {
  role: RoleWithBest;
  handleAction: (action: DetailedPlayerAction) => void;
  isActive: boolean;
  isDisabled: boolean;
}) => {
  const roleName = useSelector(
    playerRoleLabelSelector({
      playerRoleId: role.playerRoleId,
      appendPosition: false,
    }),
  );

  const tier = useSelector(
    tierSelector(+role.metaRatingZeroChem, role.playerRoleId),
  );

  return (
    <button
      className={cn(
        "flex flex-row h-9 mobile:h-7 rounded-md px-2 justify-end items-center group max-w-50 mobile:max-w-76 w-full",
        isDisabled
          ? "bg-disabled cursor-default "
          : isActive
            ? "bg-action-tertiary-active"
            : "bg-action-tertiary hover:bg-action-tertiary-hover",
      )}
      disabled={isDisabled}
      onClick={() => {
        handleAction({
          type: "SET_PLAYER_ROLE_ID",
          payload: role.playerRoleId,
        });
      }}
    >
      <div
        className={cn(
          "text-xs text-dark-tertiary text-left flex-1 min-w-0 touch-device:w-px flex flex-row items-center gap-1",
          isDisabled
            ? "text-dark-disabled"
            : isActive
              ? "text-dark-primary"
              : "group-hover:text-dark-secondary text-dark-secondary",
        )}
      >
        <span className="truncate">{roleName}</span>
        {(role.plus || role.plusPlus) && (
          <PlusPlus plus={role.plus} plusPlus={role.plusPlus} />
        )}
      </div>
      <div
        className={cn(
          "flex flex-row items-center text-dark-secondary flex-shrink-0",
          isActive && !isDisabled && "text-dark-primary",
        )}
      >
        <div className="w-4">
          {role.best && <div className={cn("w-3 h-3")}>{star}</div>}
        </div>

        <p className="text-xs text-right w-6">
          {+role.metaRating > 0 ? role.metaRating : "-"}
        </p>
        {tier?.tierName && (
          <div
            className={cn(
              "relative",
              isDisabled
                ? "text-dark-disabled"
                : isActive
                  ? "text-dark-secondary"
                  : "group-hover:text-dark-secondary text-dark-tertiary",
            )}
          >
            <p className="text-xs text-right w-5 h-5 items-center justify-center flex rounded-xs ml-1 font-bold">
              {tier?.splitTierName?.[0]}
            </p>
            {tier?.splitTierName?.[1] && (
              <p className="absolute top-0 right-0 text-tiny">
                {tier?.splitTierName?.[1]}
              </p>
            )}
          </div>
        )}
      </div>
    </button>
  );
};

export const RoleSection = ({
  currentPlayerRoleId,
  position,
  roles,
  handleAction,
  isDisabled,
}: {
  currentPlayerRoleId: number;
  position: string;
  roles: RoleWithBest[];
  handleAction: (action: DetailedPlayerAction) => void;
  isDisabled: boolean;
}) => {
  return (
    <div className="flex flex-col gap-y-1">
      <h3 className="text-xs text-dark-tertiary mx-2">{position}</h3>
      <div className="flex flex-col gap-y-1">
        {roles.map((role) => (
          <Role
            key={role.playerRoleId}
            role={role}
            isActive={currentPlayerRoleId === role.playerRoleId}
            handleAction={handleAction}
            isDisabled={isDisabled}
          />
        ))}
      </div>
    </div>
  );
};

export const RolesSection = ({
  rolesPlus,
  metaRatingsAllRoles,
  currentChemistry,
  currentPlayerRoleId,
  handleAction,
  handleShowAllRoles,
  positions,
  chemStylesOpen,
}: {
  rolesPlus: number[];
  metaRatingsAllRoles: MetaRating[];
  currentChemistry: Chemistry;
  currentPlayerRoleId: number;
  handleAction: (action: DetailedPlayerAction) => void;
  handleShowAllRoles: () => void;
  positions: string[];
  chemStylesOpen: boolean;
}) => {
  const isTouchDevice = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);

  const sortedRoles = groupAndSortRolesByPosition(
    rolesPlus,
    metaRatingsAllRoles,
    currentChemistry,
    isTouchDevice,
    positions,
  );

  return (
    <GridBox
      title="Roles Plus"
      className="col-span-4 touch-device:col-span-1 touch-device:min-h-[528px] max-touch-device:-mx-4"
      noLayout={isTouchDevice}
    >
      <ScrollArea
        className={cn(
          "touch-device:px-4 pb-4 relative",
          chemStylesOpen ? "touch-device:h-150" : "touch-device:h-114",
        )}
      >
        <div className="flex flex-row overflow-x-auto touch-device:flex-col gap-3 max-touch-device:px-4">
          {Object.entries(sortedRoles).map(([position, roles]) => (
            <RoleSection
              key={position}
              position={position}
              roles={roles}
              currentPlayerRoleId={currentPlayerRoleId}
              handleAction={handleAction}
              isDisabled={false}
            />
          ))}
        </div>
        <ScrollBar
          orientation="vertical"
          className="cursor-grab z-10"
          forceMount
        />
      </ScrollArea>
      <div className="mt-auto touch-device:mt-2 hidden touch-device:flex touch-device:justify-center">
        <Button
          label="Show All Roles"
          onClick={handleShowAllRoles}
          variant="tertiary"
        />
      </div>
    </GridBox>
  );
};
